/**
 * MailIntegrationSection - секция «Корпоративная почта».
 *
 * Подключение через /api/v2/mail: пользователь определяется сервером по сессии, логин и пароль проверяются
 * входом в почтовый сервер, пароль хранится зашифрованным и наружу не отдается (поле пароля всегда пустое).
 * Состояния: сессия действует / данные сохранены, но сервер почты не ответил / требуется вход.
 * Выход из почты стирает сохраненный пароль - только после подтверждения.
 */
import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Button, IconButton, InputAdornment, Alert, CircularProgress, Typography } from '@mui/material';
import {
  MailOutline,
  CheckCircle,
  Visibility,
  VisibilityOff,
  WarningAmber,
  LogoutOutlined,
  RefreshOutlined,
  VpnKeyOutlined
} from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { ConfirmDialog } from '../../admin/reports/edit/EditCommon';
import { connectMail, disconnectMail, MailState } from '../profileApi';

export interface MailIntegrationSectionProps {
  username: string;
  mail: MailState;
  /** Почта входит в заполненность профиля и еще не подключена */
  required: boolean;
  onChanged: () => Promise<void>;
}

const MAIL_DOMAIN = 'zerdegroup.kz';
const MAIL_LOGIN_PATTERN = /^[^\s@]+(@[^\s@]+)?$/;
const MAX_MAIL_LOGIN = 150;
const MAX_MAIL_PASSWORD = 100;

const loginOf = (mail: MailState, username: string): string =>
  mail.email || (username ? `${username.toLowerCase()}@${MAIL_DOMAIN}` : '');

/** «2026-10-23T09:15:00.000Z» -> «23.10.2026, 14:15» */
const formatExpiresAt = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusText = (text: string, color: string) => (
  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, lineHeight: 1 }}>{text}</Typography>
);

// Раскладка - по ширине секции, а не окна: рядом с боковым меню профиля секция заметно уже окна.
// Плитки сессии и поля логина с паролем - в ряд, когда обоим хватает места, иначе одно под другим
const PAIR_WIDE = '@container (min-width: 520px)';
const pairGridSx = { display: 'grid', gridTemplateColumns: '1fr', [PAIR_WIDE]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } } as const;

const tileLabelSx = {
  color: '#94a3b8',
  fontSize: '0.68rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'block'
} as const;

const MailIntegrationSection: React.FC<MailIntegrationSectionProps> = ({ username, mail, required, onChanged }) => {
  const [login, setLogin] = useState(() => loginOf(mail, username));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Сменился адрес или статус почты на сервере - логин из него, пароль никогда не подставляется.
  // Перезагрузка профиля другими секциями с теми же данными набранное не стирает
  useEffect(() => {
    setLogin(mail.email || (username ? `${username.toLowerCase()}@${MAIL_DOMAIN}` : ''));
    setPassword('');
  }, [mail.email, mail.active, username]);

  const showForm = !mail.active || editing;
  const trimmedLogin = login.trim();
  const loginError = !trimmedLogin
    ? submitted ? 'Укажите логин почты' : ''
    : trimmedLogin.length > MAX_MAIL_LOGIN || !MAIL_LOGIN_PATTERN.test(trimmedLogin)
      ? 'Логин почты указан некорректно'
      : '';
  const passwordError = !password.trim()
    ? submitted ? 'Укажите пароль почты' : ''
    : password.trim().length > MAX_MAIL_PASSWORD
      ? 'Пароль почты слишком длинный'
      : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setAuthError(null);
    if (!trimmedLogin || !password.trim() || loginError || passwordError) return;

    setSaving(true);
    try {
      const result = await connectMail(trimmedLogin, password.trim());
      notify.success(result.message);
      setPassword('');
      setSubmitted(false);
      setEditing(false);
      await onChanged();
    } catch (err) {
      setAuthError(apiErrorMessage(err, 'Не удалось подключить почту'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setSubmitted(false);
    setAuthError(null);
    setLogin(loginOf(mail, username));
    setPassword('');
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setConfirmError(null);
    try {
      notify.success(await disconnectMail());
      setConfirmOpen(false);
      setEditing(false);
      await onChanged();
    } catch (err) {
      setConfirmError(apiErrorMessage(err, 'Не удалось отключить почту'));
    } finally {
      setDisconnecting(false);
    }
  };

  const badge = mail.active ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CheckCircle sx={{ fontSize: 14, color: ZERDE_COLORS.emerald }} />
      {statusText('Подключена', '#047857')}
    </Box>
  ) : mail.unavailable && mail.configured ? (
    statusText('Сервер почты не отвечает', '#64748b')
  ) : (
    statusText('Требуется вход', '#d97706')
  );

  const highlighted = required && !mail.active;

  return (
    <Paper
      id="section-mail"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: highlighted ? '2px solid #fed7aa' : '1px solid #e2e8f0',
        boxShadow: highlighted ? '0 4px 20px rgba(234, 88, 12, 0.08)' : '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px',
        containerType: 'inline-size'
      }}
    >
      <SectionHeaderDivider
        id="header-mail"
        icon={<MailOutline sx={{ fontSize: 22 }} />}
        title="Корпоративная почта"
        subtitle="Вход в почту портала: письма и вложения открываются без повторного ввода пароля"
        badge={badge}
      />

      {mail.active && !editing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '16px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem', lineHeight: 1.2, wordBreak: 'break-all' }}>
                {mail.email || loginOf(mail, username)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Сессия продлевается автоматически, пока сохранен пароль
              </Typography>
            </Box>
            <Box sx={{ ...pairGridSx, gap: 1.5 }}>
              <Box sx={{ p: 1.5, px: 2, borderRadius: '12px', bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={tileLabelSx}>
                  Хранение пароля
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  Зашифрован на сервере
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, px: 2, borderRadius: '12px', bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={tileLabelSx}>
                  Сессия действует до
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5, fontFamily: 'monospace' }}>
                  {formatExpiresAt(mail.expiresAt)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={() => setEditing(true)}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#cbd5e1',
                color: '#334155',
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
              }}
            >
              Сменить пароль почты
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutOutlined />}
              onClick={() => {
                setConfirmError(null);
                setConfirmOpen(true);
              }}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#fed7aa',
                color: '#c2410c',
                '&:hover': { borderColor: '#ea580c', bgcolor: '#fff7ed' }
              }}
            >
              Выйти из почты
            </Button>
          </Box>
        </Box>
      ) : null}

      {showForm && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {!mail.active && mail.unavailable && mail.configured && (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              Почтовый сервер сейчас не отвечает, подключение не проверено. Сохраненные данные остаются в силе - вводить
              пароль заново не нужно.
            </Alert>
          )}

          {!mail.active && !mail.unavailable && mail.configured && (
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="inherit" />}
              sx={{ borderRadius: '12px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', color: '#9a3412' }}
            >
              {(mail.message || 'Сохраненный пароль почты больше не подходит').replace(/\.$/, '')}. Введите действующий
              пароль почты.
            </Alert>
          )}

          {!mail.configured && highlighted && (
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="inherit" />}
              sx={{ borderRadius: '12px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', color: '#9a3412' }}
            >
              Подключите корпоративную почту. Логин и пароль проверяются входом в почтовый сервер.
            </Alert>
          )}

          {authError && (
            <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setAuthError(null)}>
              {authError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ ...pairGridSx, gap: 2 }}>
              <TextField
                label="Логин почты"
                required
                fullWidth
                size="small"
                value={login}
                onChange={e => {
                  setAuthError(null);
                  setLogin(e.target.value);
                }}
                error={Boolean(loginError)}
                helperText={loginError || ' '}
                placeholder={`user@${MAIL_DOMAIN}`}
                autoComplete="username"
                inputProps={{ maxLength: MAX_MAIL_LOGIN }}
              />
              <TextField
                label="Пароль почты"
                required
                type={showPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={password}
                onChange={e => {
                  setAuthError(null);
                  setPassword(e.target.value);
                }}
                error={Boolean(passwordError)}
                helperText={passwordError || ' '}
                autoComplete="current-password"
                inputProps={{ maxLength: MAX_MAIL_PASSWORD }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <VpnKeyOutlined />}
                disabled={saving}
                sx={{
                  bgcolor: ZERDE_COLORS.primary,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                {saving ? 'Проверка входа...' : 'Подключить почту'}
              </Button>
              {mail.active && editing && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#64748b' }}
                >
                  Отмена
                </Button>
              )}
              {!mail.active && mail.configured && (
                <Button
                  variant="text"
                  onClick={() => {
                    setConfirmError(null);
                    setConfirmOpen(true);
                  }}
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#c2410c' }}
                >
                  Удалить сохраненный пароль
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={mail.active ? 'Выйти из почты?' : 'Удалить сохраненный пароль?'}
        confirmText={mail.active ? 'Выйти' : 'Удалить'}
        confirmColor="error"
        maxWidth="xs"
        loading={disconnecting}
        onConfirm={handleDisconnect}
        onClose={() => {
          if (!disconnecting) setConfirmOpen(false);
        }}
      >
        <Typography variant="body2" sx={{ color: '#334155' }}>
          Сессия почты будет завершена, сохраненный пароль удален. Чтобы снова открыть почту в портале, потребуется
          ввести пароль заново.
        </Typography>
        {confirmError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
            {confirmError}
          </Alert>
        )}
      </ConfirmDialog>
    </Paper>
  );
};

export default MailIntegrationSection;
